import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { formatScheduleForDisplay } from 'src/helpers/scheduler-schedule.helpers';
import type { RegisteredSchedulerTask, ScheduledTask } from '../types/scheduler.types';
import { SchedulerRunHistory } from './scheduler-run-history';
import { SchedulerTaskForm, type SchedulerTaskFormValues } from './scheduler-task-form';
import { SchedulerDsButton } from './scheduler-ds-button';

export type SchedulerTaskSheetMode = 'create' | 'edit';

interface SchedulerTaskSheetProps {
  open: boolean;
  mode: SchedulerTaskSheetMode;
  task: ScheduledTask | null;
  registry: RegisteredSchedulerTask[];
  isSaving?: boolean;
  isDeleting?: boolean;
  submitError?: string | null;
  deleteError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: SchedulerTaskFormValues) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
}

export function SchedulerTaskSheet({
  open,
  mode,
  task,
  registry,
  isSaving = false,
  isDeleting = false,
  submitError = null,
  deleteError = null,
  onOpenChange,
  onSubmit,
  onDelete,
}: SchedulerTaskSheetProps) {
  const isCreate = mode === 'create';
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const isBusy = isSaving || isDeleting;

  const handleConfirmDelete = async() => {
    if (!onDelete) {
      return;
    }
    try {
      await onDelete();
      setConfirmDeleteOpen(false);
    } catch {
      // deleteError is surfaced in the sheet; keep the dialog open.
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-xl"
      >
        <div className="border-b border-gray-400 px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-xl font-medium text-black">
              {isCreate ? 'New scheduled task' : task?.name ?? 'Task details'}
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              {isCreate
                ? 'Pick a registered task and schedule it with a daily time or cron expression.'
                : 'Update the schedule or review recent runs for this task.'}
            </SheetDescription>
          </SheetHeader>
          {!isCreate && task ? (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-gray-500">
                  Next run
                </dt>
                <dd className="mt-0.5 text-black">
                  {task.next_run_at ? formatDisplayDateTime(task.next_run_at) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">
                  Schedule
                </dt>
                <dd className="mt-0.5 font-mono text-sm text-black">
                  {formatScheduleForDisplay(task)}
                </dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="flex-1 px-6 py-5">
          <SchedulerTaskForm
            registry={registry}
            initialTask={isCreate ? null : task}
            isSaving={isBusy}
            submitError={submitError}
            onSubmit={onSubmit}
            onCancel={() => onOpenChange(false)}
          />

          {!isCreate && task ? (
            <section
              className="mt-8 border-t border-gray-300 pt-6"
              aria-labelledby="scheduler-run-history-heading"
            >
              <h3
                id="scheduler-run-history-heading"
                className="text-sm font-medium uppercase tracking-wide text-gray-500"
              >
                Run history
              </h3>
              <div className="mt-4">
                <SchedulerRunHistory task={task} />
              </div>
            </section>
          ) : null}

          {!isCreate && task && onDelete ? (
            <section className="mt-8 border-t border-gray-300 pt-6">
              <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">
                Danger zone
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Remove this schedule from the database. Run history for this schedule will also be deleted.
              </p>
              {deleteError ? (
                <p
                  className="mt-3 text-sm text-red-700"
                  role="alert"
                >
                  {deleteError}
                </p>
              ) : null}
              <SchedulerDsButton
                type="button"
                variant="danger-ghost"
                className="mt-4"
                disabled={isBusy}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete schedule
              </SchedulerDsButton>
            </section>
          ) : null}
        </div>

        {isBusy ? (
          <div
            className="sticky bottom-0 flex items-center gap-2 border-t border-gray-400 bg-black px-6 py-3 text-sm text-white"
            role="status"
          >
            <Loader2 className="size-4 animate-spin" />
            {isDeleting ? 'Deleting…' : 'Saving…'}
          </div>
        ) : null}

        <Dialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
        >
          <DialogContent className="bg-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                Delete scheduled task?
              </DialogTitle>
              <DialogDescription>
                {task?.name
                  ? `"${task.name}" will be removed and will no longer run. This cannot be undone.`
                  : 'This schedule will be removed and will no longer run. This cannot be undone.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <SchedulerDsButton
                type="button"
                variant="secondary"
                disabled={isDeleting}
                onClick={() => setConfirmDeleteOpen(false)}
              >
                Cancel
              </SchedulerDsButton>
              <SchedulerDsButton
                type="button"
                variant="danger"
                disabled={isDeleting}
                onClick={() => void handleConfirmDelete()}
              >
                Delete
              </SchedulerDsButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
